/**
 * Chart design tokens: one validated palette, and the formatters every chart
 * shares.
 *
 * The palette is chosen for looks AND checked, never one without the other. Each
 * set was searched and checked with a colour-vision validator (the dataviz skill's
 * validate_palette.js) against the surface it actually sits on, over ALL pairs of
 * the five roles the charts draw - any two can share a chart - and passes every gate:
 *
 *   lightness band, chroma floor, colour-vision separation (worst pair ΔE 8.4
 *   light / 11.9 dark / 10.6 sepia, against a target of 8), and normal-vision
 *   separation (18.2 / 17.1 / 15.5, against a floor of 15).
 *
 * The obvious "nicer" palettes fail when measured: matplotlib's tab10 separates
 * orange from green by ΔE 0.7 for a protanope, and a blue/violet pair at the
 * same lightness is indistinguishable in dark mode. Hues were kept per role and
 * lightness searched per mode, so each mode is its own steps of the same colours.
 *
 * The previous set (Okabe-Ito, October 2026 and before) separated well but read
 * as unrelated hues side by side; the one before that failed four gates outright.
 *
 * Two light-mode hues (aqua, amber) sit below 3:1 contrast on white. That is
 * permitted only with relief: every chart carries a legend, and every chart
 * offers a table view of the same numbers. Do not remove either.
 *
 * RULES, if you add a series:
 *   - Assign the next slot in order. Never cycle, never invent a hue.
 *   - Past six, fold into "Other" or split the chart. A seventh hue has not
 *     been validated and cannot be assumed to separate.
 *   - Colour follows the entity, not its rank: revenue is always slot 1, so a
 *     filter that removes a series must not repaint the others.
 */

export type Mode = 'light' | 'dark' | 'sepia' | 'ledger' | 'graphite' | 'terminal' | 'contrast';

/*
 * October 2026: the Okabe-Ito set above was replaced because it read as unrelated hues side by
 * side - safe, but not pleasant. These are the calmer set the accounting charts now share:
 * revenue blue, costs brick, cash aqua, capital amber, profit a deep violet (liability, a muted
 * magenta, is drawn by no v2 chart). Searched and checked with the dataviz skill's validator
 * over ALL pairs of the five charted roles, not only neighbours, since any two can share a
 * chart: light on #ffffff - worst CVD ΔE 8.4, normal 18.2; dark on #0f172a - 11.9 / 17.1, all
 * >= 3:1; also passing on ledger, graphite and terminal. Light aqua and amber sit below 3:1 on
 * white, which the legend and table view relieve, as before.
 */
const LIGHT_SET = ['#2a78d6', '#d9483b', '#1baf7a', '#eda100', '#e87ba4', '#5a2fa8'] as const;
const DARK_SET = ['#6c92e5', '#b63920', '#30a68b', '#b68420', '#d55181', '#8c49ca'] as const;

/**
 * The six validated categorical slots, in fixed assignment order.
 *
 * Only three sets exist, and the later themes reuse them rather than inventing
 * a fourth that has not been through the validator. Each reuse sits on a
 * surface at least as far from its hues as the one it was checked against:
 * Ledger (#fafcf6) and High contrast (#ffffff) are white for this purpose, so
 * they carry light's legend-and-table relief; Graphite (#1e1d1b) is within a
 * step of #1a1a19, the surface the dark set was first validated on; Terminal
 * (#0e0c08) is darker than slate, which only raises every slot's contrast.
 */
export const CATEGORICAL: Record<Mode, readonly string[]> = {
  light: LIGHT_SET,
  dark: DARK_SET,
  // Its own steps of the same hues, for #f6eeda: worst CVD ΔE 10.6, normal 15.5, all >= 3:1.
  sepia: ['#1c4d9c', '#8e3329', '#298e70', '#aa6b0e', '#a8326e', '#5826ed'],
  ledger: LIGHT_SET,
  graphite: DARK_SET,
  terminal: DARK_SET,
  contrast: LIGHT_SET,
};

/**
 * Series roles.
 *
 * Named by what the series IS, so the same measure keeps its colour across
 * every chart it appears in — revenue is slot 1 on the trend chart and on the
 * breakdown, and a reader learns the mapping once.
 */
export const SERIES = {
  revenue: 0,
  cost: 1,
  cash: 2,
  capital: 3,
  liability: 4,
  profit: 5,
} as const;

export type SeriesRole = keyof typeof SERIES;

/**
 * The colour for a series role.
 *
 * With no mode, returns the themed variable - what charts want, so they
 * follow the theme. With an explicit mode, returns the literal hex, which is
 * what tests and the palette validator need.
 */
export function seriesColor(role: SeriesRole, mode?: Mode): string {
  const slot = SERIES[role];
  if (mode) return CATEGORICAL[mode][slot];
  return withFallback(CAT_VAR(slot), CATEGORICAL.light[slot]);
}

/** Ink and surface. Text never wears a series colour. */
export const INK = {
  light: {
    surface: '#ffffff',
    primary: '#1f2933',
    secondary: '#52514e',
    muted: '#8a8f98',
    grid: '#eceff3',
    axis: '#c9ced6',
  },
  dark: {
    // Slate, matching the themed card surface - see the note above.
    surface: '#0f172a',
    primary: '#f8fafc',
    secondary: '#cbd5e1',
    muted: '#94a3b8',
    grid: '#1e293b',
    axis: '#334155',
  },
  sepia: {
    surface: '#f6eeda',
    primary: '#2c2216',
    secondary: '#615038',
    muted: '#7c694e',
    grid: '#e6dabe',
    axis: '#c8b89c',
  },
  // The rest match their theme's ramp in globals.css: surface is --c-white,
  // primary gray-900, secondary gray-600, muted gray-500, grid gray-100.
  ledger: {
    surface: '#fafcf6',
    primary: '#151f17',
    secondary: '#425041',
    muted: '#566454',
    grid: '#e7ede3',
    axis: '#b8c4b3',
  },
  graphite: {
    surface: '#1e1d1b',
    primary: '#f6f3ed',
    secondary: '#c6c0b7',
    muted: '#a49e95',
    grid: '#2f2d2a',
    axis: '#56524d',
  },
  terminal: {
    surface: '#0e0c08',
    primary: '#ffd68e',
    secondary: '#d6a85a',
    muted: '#ba924c',
    grid: '#1e1a11',
    axis: '#4e4129',
  },
  contrast: {
    surface: '#ffffff',
    primary: '#000000',
    secondary: '#282828',
    muted: '#383838',
    grid: '#eaeaea',
    axis: '#606060',
  },
} as const;

/* ── Theming ───────────────────────────────────────────────────────────────
 *
 * Charts re-colour through CSS variables rather than through React state.
 *
 * Recharts writes its colours into SVG `fill` and `stroke` attributes, and a
 * var() resolves there and updates live when the variable changes - verified
 * in a browser before this was built on. So a theme switch repaints every
 * chart with no re-render, no context and no prop drilling, and none of the
 * ~950 lines of chart code had to change.
 *
 * Every var carries its LIGHT value as the fallback. v1 never sets these
 * variables, so v1 renders the exact hexes it renders today - the apps stay
 * separate by construction, not by inspection.
 *
 * The hexes above remain the single source of truth: applyChartTheme writes
 * them into the variables, so there is no second copy in a stylesheet to
 * drift from them.
 */

const CAT_VAR = (i: number) => `--chart-${i + 1}`;
const INK_VAR = (key: keyof typeof INK.light) => `--chart-ink-${key}`;

function withFallback(variable: string, fallback: string): string {
  return `var(${variable}, ${fallback})`;
}

/** Write a mode's palette into the document, repainting every chart. */
export function applyChartTheme(mode: Mode): void {
  const root = document.documentElement;
  CATEGORICAL[mode].forEach((hex, i) => {
    root.style.setProperty(CAT_VAR(i), hex);
  });
  const ink = INK[mode];
  (Object.keys(ink) as (keyof typeof ink)[]).forEach((key) => {
    root.style.setProperty(INK_VAR(key), ink[key]);
  });
}

/** Ink, as a themed var with the light value as its fallback. */
function inkVar(key: keyof typeof INK.light): string {
  return withFallback(INK_VAR(key), INK.light[key]);
}

// ─── Formatting ──────────────────────────────────────────────

/**
 * Re-exported so the charts keep the name they already use, while the map
 * itself lives in lib/currency.ts - one list, shared with the views, which
 * otherwise drifted: figures read "GBP 8,122,680" outside the charts and
 * "£8,122,680" inside them.
 */
import { currencySymbol } from '../lib/currency';

export { currencySymbol as currencyPrefix };

/**
 * Axis ticks: short, with the unit stated once in the axis label rather than
 * repeated on every tick.
 */
export function fmtAxis(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)}bn`;
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}m`;
  if (abs >= 1_000) return `${Math.round(v / 1_000)}k`;
  return String(Math.round(v));
}

/** Tooltips and table cells: the full figure, with its currency. */
export function fmtFull(v: number | undefined, currency?: string): string {
  const n = v ?? 0;
  const sign = n < 0 ? '-' : '';
  return `${sign}${currencySymbol(currency)}${Math.abs(n).toLocaleString('en-GB', {
    maximumFractionDigits: 0,
  })}`;
}

export function fmtPercent(v: number | undefined): string {
  return `${((v ?? 0) * 100).toFixed(1)}%`;
}

/** Shared axis/grid props, so every chart is recessive in the same way. */
export function axisProps() {
  return {
    tick: { fontSize: 11, fill: inkVar('secondary') },
    axisLine: { stroke: inkVar('axis') },
    tickLine: false as const,
  };
}

export function gridProps() {
  return {
    stroke: inkVar('grid'),
    strokeDasharray: '0',
    vertical: false as const,
  };
}

export function tooltipProps() {
  return {
    contentStyle: {
      background: inkVar('surface'),
      border: `1px solid ${inkVar('grid')}`,
      borderRadius: 6,
      fontSize: 12,
      color: inkVar('primary'),
      boxShadow: '0 4px 12px rgba(0,0,0,.08)',
    },
    labelStyle: { color: inkVar('secondary'), marginBottom: 4 },
  };
}
